import { DungeonDefinition } from '../types/dungeon.types';

// ---------------------------------------------------------------------------
// Dungeons — 11-dungeon skeleton + test arena
// Floor counts, averageMonsterLevels, bossLevels = playtest calibration.
// Biomes (construction, spawn table, locations) live in `biomes.config.ts`;
// a dungeon maps floor ranges to biome ids via `biomeBands`, and overrides
// specific floors (boss, pause) via `customFloors`.
// ---------------------------------------------------------------------------

// --- Campaign ---

const CLEAR_FOREST_DUNGEON: DungeonDefinition = {
  id: 'clear-forest',
  label: 'Forêt claire',
  difficulty: 1,
  mapMarker: { x: 0.739, y: 0.314 },
  initialConfig: {
    averageMonsterLevel: 3,
    floorCount: 6,
    biomeBands: [{ biomeId: 'clear-forest', floorRange: [1, 6] }],
  },
};

const DAMP_CAVES_DUNGEON: DungeonDefinition = {
  id: 'damp-caves',
  label: 'Grottes humides',
  difficulty: 2,
  mapMarker: { x: 0.365, y: 0.456 },
  initialConfig: {
    averageMonsterLevel: 8,
    floorCount: 10,
    biomeBands: [{ biomeId: 'damp-caves', floorRange: [1, 9] }],
    customFloors: [
      {
        floor: 10,
        floorType: 'boss',
        bossMonsterId: 'shoebill',
        bossLevel: 11,
        biomeId: 'damp-caves',
        style: 'open',
        width: 25,
        height: 25,
      },
    ],
  },
};

const OLD_RUINS_DUNGEON: DungeonDefinition = {
  id: 'old-ruins',
  label: 'Ruines anciennes',
  difficulty: 2,
  mapMarker: { x: 0.593, y: 0.612 },
  initialConfig: {
    averageMonsterLevel: 14,
    floorCount: 12,
    biomeBands: [{ biomeId: 'old-ruins', floorRange: [1, 12] }],
  },
};

const LOST_GALLERIES_DUNGEON: DungeonDefinition = {
  id: 'lost-galleries',
  label: 'Galeries oubliées',
  difficulty: 3,
  mapMarker: { x: 0.612, y: 0.282 },
  initialConfig: {
    averageMonsterLevel: 17,
    floorCount: 10,
    biomeBands: [{ biomeId: 'lost-galleries', floorRange: [1, 10] }],
  },
};

const THORN_MAZE_DUNGEON: DungeonDefinition = {
  id: 'thorn-maze',
  label: "Dédale d'épines",
  difficulty: 3,
  mapMarker: { x: 0.819, y: 0.638 },
  initialConfig: {
    averageMonsterLevel: 20,
    floorCount: 13,
    biomeBands: [{ biomeId: 'thorn-maze', floorRange: [1, 12] }],
    customFloors: [
      {
        floor: 13,
        floorType: 'boss',
        bossMonsterId: 'medusa',
        bossLevel: 23,
        biomeId: 'thorn-maze',
        style: 'open',
        width: 25,
        height: 25,
      },
    ],
  },
};

const WINDY_PEAK_DUNGEON: DungeonDefinition = {
  id: 'windy-peak',
  label: 'Pic venteux',
  difficulty: 3,
  mapMarker: { x: 0.762, y: 0.485 },
  initialConfig: {
    averageMonsterLevel: 23,
    floorCount: 12,
    biomeBands: [{ biomeId: 'windy-peak', floorRange: [1, 12] }],
  },
};

const DEEP_CAVERNS_DUNGEON: DungeonDefinition = {
  id: 'deep-caverns',
  label: 'Cavernes profondes',
  difficulty: 4,
  mapMarker: { x: 0.732, y: 0.825 },
  initialConfig: {
    averageMonsterLevel: 26,
    floorCount: 16,
    biomeBands: [{ biomeId: 'deep-caverns', floorRange: [1, 16] }],
  },
};

const SUMMIT_KEEP_DUNGEON: DungeonDefinition = {
  id: 'summit-keep',
  label: 'Citadelle du sommet',
  difficulty: 5,
  mapMarker: { x: 0.071, y: 0.547 },
  initialConfig: {
    averageMonsterLevel: 30,
    floorCount: 18,
    biomeBands: [{ biomeId: 'summit-keep', floorRange: [1, 17] }],
    customFloors: [
      {
        floor: 18,
        floorType: 'boss',
        bossMonsterId: 'dragon',
        bossLevel: 33,
        biomeId: 'summit-keep',
        style: 'open',
        width: 25,
        height: 25,
      },
    ],
  },
};

const STILL_PONDS_DUNGEON: DungeonDefinition = {
  id: 'still-ponds',
  label: 'Étangs dormants',
  difficulty: 5,
  mapMarker: { x: 0.464, y: 0.748 },
  initialConfig: {
    averageMonsterLevel: 38,
    floorCount: 25,
    biomeBands: [{ biomeId: 'still-ponds', floorRange: [1, 25] }],
  },
};

const CALM_GLADE_DUNGEON: DungeonDefinition = {
  id: 'calm-glade',
  label: 'Clairière paisible',
  difficulty: 5,
  mapMarker: { x: 0.506, y: 0.324 },
  initialConfig: {
    averageMonsterLevel: 38,
    floorCount: 12,
    biomeBands: [{ biomeId: 'calm-glade', floorRange: [1, 12] }],
  },
};

const ENDLESS_RIFT_DUNGEON: DungeonDefinition = {
  id: 'endless-rift',
  label: 'Faille sans fond',
  difficulty: 5,
  mapMarker: { x: 0.941, y: 0.518 },
  initialConfig: {
    averageMonsterLevel: 38,
    floorCount: 50,
    biomeBands: [
      { biomeId: 'endless-rift', floorRange: [1, 33] },
      { biomeId: 'endless-rift-deep', floorRange: [34, 50] },
    ],
  },
};

// --- Test arena (spell testing) ---

const TEST_ARENA_DUNGEON: DungeonDefinition = {
  id: 'test-arena',
  label: 'Arène de test',
  difficulty: 1,
  mapMarker: { x: 0.28, y: 0.615 },
  initialConfig: {
    averageMonsterLevel: 1,
    floorCount: 1,
    biomeBands: [{ biomeId: 'test', floorRange: [1, 1] }],
  },
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const DUNGEON_REGISTRY: Record<string, DungeonDefinition> = {
  'test-arena': TEST_ARENA_DUNGEON,
  'clear-forest': CLEAR_FOREST_DUNGEON,
  'damp-caves': DAMP_CAVES_DUNGEON,
  'old-ruins': OLD_RUINS_DUNGEON,
  'lost-galleries': LOST_GALLERIES_DUNGEON,
  'thorn-maze': THORN_MAZE_DUNGEON,
  'windy-peak': WINDY_PEAK_DUNGEON,
  'deep-caverns': DEEP_CAVERNS_DUNGEON,
  'summit-keep': SUMMIT_KEEP_DUNGEON,
  'still-ponds': STILL_PONDS_DUNGEON,
  'calm-glade': CALM_GLADE_DUNGEON,
  'endless-rift': ENDLESS_RIFT_DUNGEON,
};

/** Default dungeon when launching "New game" or as a runtime fallback. */
export const DEFAULT_DUNGEON_ID = 'clear-forest';

// ---------------------------------------------------------------------------
// Completion rewards
// ---------------------------------------------------------------------------

export interface DungeonCompletionReward {
  /** Dungeon IDs unlocked on first completion. */
  unlocks: string[];
  /** Spell IDs whose grimoires (`grimoire-<id>`) are added to the inventory on first completion. */
  grantSpells?: string[];
  /** Major trinket model IDs granted on first completion (added to loadout). */
  grantMajors?: string[];
  /** Minor trinket model IDs granted on first completion (added to loadout). */
  grantMinors?: string[];
  /** Sketch model IDs granted on first completion (added to loadout). */
  grantSketches?: string[];
}

export const DUNGEON_COMPLETION_REWARDS: Record<
  string,
  DungeonCompletionReward
> = {
  // --- Campaign ---
  'clear-forest': {
    unlocks: ['damp-caves'],
    grantSpells: ['joyful-rush'],
    grantMajors: ['pocketknife'],
    grantMinors: ['vigor-band'],
  },
  'damp-caves': { unlocks: ['old-ruins'], grantSpells: ['gloom'] },
  'old-ruins': {
    unlocks: ['thorn-maze', 'lost-galleries'],
    grantSpells: ['fear-strike'],
    grantMajors: ['walkman'],
    grantMinors: ['fury-charm'],
  },
  'lost-galleries': {
    unlocks: [],
    grantSpells: ['gale-cone'],
    grantMajors: ['camera'],
    grantMinors: ['swift-cast'],
  },
  'thorn-maze': {
    unlocks: ['deep-caverns', 'windy-peak'],
    grantSpells: ['venom-bite'],
  },
  'windy-peak': {
    unlocks: [],
    grantMajors: ['baseball-glove'],
    grantMinors: ['keen-edge'],
  },
  'deep-caverns': { unlocks: ['summit-keep'], grantMinors: ['long-reach'] },
  'summit-keep': { unlocks: ['still-ponds', 'calm-glade', 'endless-rift'] },
  'calm-glade': { unlocks: [], grantMinors: ['bright-charm'] },
  // still-ponds / endless-rift: no completion reward (post-game)
};
